import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/Einwohnermeldeamt/SEOHead';
import BundeslandCard from '../../components/Einwohnermeldeamt/BundeslandCard';
import LocationCard from '../../components/Einwohnermeldeamt/LocationCard';
import LocationCTA from '../../components/Einwohnermeldeamt/LocationCTA';
import { getBundeslaender, formatNumber, getEinwohnermeldeaemter } from '../../utils/einwohnermeldeamtHelpers';
import { BundeslandInfo, Einwohnermeldeamt } from '../../types/einwohnermeldeamt';

const EinwohnermeldeamtIndex: React.FC = () => {
  const [bundeslaender, setBundeslaender] = useState<BundeslandInfo[]>([]);
  const [allLocations, setAllLocations] = useState<Einwohnermeldeamt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'population' | 'plz'>('name');

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [bundeslaenderData, locationsData] = await Promise.all([
          getBundeslaender(),
          getEinwohnermeldeaemter()
        ]);
        setBundeslaender(bundeslaenderData);
        setAllLocations(locationsData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter and sort all locations
  const filteredAndSortedLocations = useMemo(() => {
    let filtered = allLocations;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allLocations.filter(
        loc =>
          loc.gemeindeName.toLowerCase().includes(query) ||
          (loc.postleitzahl && loc.postleitzahl.includes(query)) ||
          (loc.kreis && loc.kreis.toLowerCase().includes(query)) ||
          (loc.ort && loc.ort.toLowerCase().includes(query)) ||
          loc.bundesland.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'population':
          return (b.einwohner || 0) - (a.einwohner || 0);
        case 'plz':
          return (a.postleitzahl || '').localeCompare(b.postleitzahl || '');
        case 'name':
        default:
          return a.gemeindeName.localeCompare(b.gemeindeName);
      }
    });

    return sorted;
  }, [allLocations, searchQuery, sortBy]);

  const isSearching = searchQuery.trim().length > 0;

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-prussian-blue mb-4"></div>
            <p className="text-gray-600">Lade Daten...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Einwohnermeldeämter in Deutschland',
    description: 'Vollständiges Verzeichnis aller Einwohnermeldeämter in Deutschland. Finden Sie Kontaktdaten, Öffnungszeiten und Informationen zu Ihrem lokalen Meldeamt.',
    url: 'https://www.adressfinder.org/einwohnermeldeamt',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Startseite',
          item: 'https://www.adressfinder.org'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Einwohnermeldeämter',
          item: 'https://www.adressfinder.org/einwohnermeldeamt'
        }
      ]
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title="Einwohnermeldeämter in Deutschland | AdressFinder.org"
        description="🏛️ Alle Einwohnermeldeämter in Deutschland ✓ Kontaktdaten ✓ Adressen ✓ Öffnungszeiten ✓ Online-Adressauskunft 24/7 verfügbar • Schnell"
        canonical="https://www.adressfinder.org/einwohnermeldeamt"
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-blue-50 to-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="max-w-3xl mx-auto text-center">
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6"
                style={{ color: '#023047', lineHeight: '1.2' }}
              >
                Einwohnermeldeämter in Deutschland
              </h1>
              
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                Finden Sie schnell und einfach Ihr zuständiges Einwohnermeldeamt. 
                Unser Verzeichnis bietet Ihnen vollständige Informationen zu allen 
                Meldeämtern in Deutschland – sortiert nach Bundesländern.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/personal-data"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-md text-white shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1"
                  style={{
                    backgroundColor: '#023047',
                    fontWeight: 600,
                    fontSize: '18px'
                  }}
                >
                  Adressauskunft starten
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 ml-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ color: '#023047' }}>
                Was ist ein Einwohnermeldeamt?
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="mb-4">
                  Das Einwohnermeldeamt, auch Meldeamt oder Bürgerbüro genannt, ist die zuständige 
                  Behörde für alle melderechtlichen Angelegenheiten. Hier müssen Sie sich bei einem 
                  Umzug an- oder abmelden, können Meldebescheinigungen beantragen oder eine 
                  Melderegisterauskunft einholen.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold mb-2" style={{ color: '#023047' }}>
                      {bundeslaender.length}
                    </div>
                    <div className="text-sm text-gray-600">Bundesländer</div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold mb-2" style={{ color: '#023047' }}>
                      {bundeslaender.reduce((sum, bl) => sum + bl.locationCount, 0)}+
                    </div>
                    <div className="text-sm text-gray-600">Meldeämter</div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <div className="text-3xl font-bold mb-2" style={{ color: '#023047' }}>
                      24/7
                    </div>
                    <div className="text-sm text-gray-600">Online verfügbar</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="py-8 px-4 bg-white border-b">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-6" style={{ color: '#023047' }}>
              Einwohnermeldeamt finden
            </h2>
            <div className="flex flex-col md:flex-row gap-4 items-end">
              {/* Search */}
              <div className="flex-1">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Suche nach Gemeinde, PLZ, Kreis oder Bundesland
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="z.B. München, 80331, Landkreis München..."
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              {/* Sort */}
              <div className="w-full md:w-64">
                <label htmlFor="sort" className="block text-sm font-medium text-gray-700 mb-2">
                  Sortieren nach
                </label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'population' | 'plz')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="name">Name (A-Z)</option>
                  <option value="population">Einwohnerzahl</option>
                  <option value="plz">Postleitzahl</option>
                </select>
              </div>
            </div>

            {/* Results count */}
            {isSearching && (
              <div className="mt-4 text-sm text-gray-600">
                {filteredAndSortedLocations.length === allLocations.length ? (
                  <span>{formatNumber(allLocations.length)} Meldeämter</span>
                ) : (
                  <span>
                    {formatNumber(filteredAndSortedLocations.length)} von {formatNumber(allLocations.length)} Meldeämtern gefunden
                  </span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Bundesländer Grid or Search Results */}
        {isSearching ? (
          <section className="py-8 px-4 bg-gray-50">
            <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
              {filteredAndSortedLocations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAndSortedLocations.map(location => (
                    <LocationCard key={location.amtlGemeindeschluessel || location.slug} location={location} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg
                    className="w-16 h-16 mx-auto mb-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-xl text-gray-600 mb-2">Keine Ergebnisse gefunden</p>
                  <p className="text-gray-500">
                    Versuchen Sie es mit einem anderen Suchbegriff
                  </p>
                </div>
              )}
            </div>
          </section>
        ) : (
          <section className="py-12 px-4 bg-gray-50">
            <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: '#023047' }}>
                Wählen Sie Ihr Bundesland
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bundeslaender.map(bundesland => (
                  <BundeslandCard key={bundesland.slug} bundesland={bundesland} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-12 px-4 bg-white">
          <div className="container mx-auto" style={{ maxWidth: '1000px' }}>
            <LocationCTA />
          </div>
        </section>

        {/* Additional Info Section */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="container mx-auto" style={{ maxWidth: '1200px' }}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ color: '#023047' }}>
                Häufige Fragen zum Einwohnermeldeamt
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#023047' }}>
                    Wann muss ich zum Einwohnermeldeamt?
                  </h3>
                  <p className="text-gray-700">
                    Sie müssen sich beim Einwohnermeldeamt melden, wenn Sie umziehen, einen 
                    Personalausweis beantragen oder verlängern möchten, oder wenn Sie eine 
                    Meldebescheinigung benötigen. Auch für Melderegisterauskünfte ist das 
                    Einwohnermeldeamt zuständig.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#023047' }}>
                    Was ist eine Melderegisterauskunft?
                  </h3>
                  <p className="text-gray-700">
                    Eine Melderegisterauskunft gibt Auskunft über die aktuelle Meldeanschrift 
                    einer Person. Diese kann bei berechtigtem Interesse beim zuständigen 
                    Einwohnermeldeamt beantragt werden. Mit AdressFinder.org können Sie diesen 
                    Prozess online und ohne Behördengang durchführen.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#023047' }}>
                    Welche Unterlagen benötige ich?
                  </h3>
                  <p className="text-gray-700">
                    Je nach Anliegen benötigen Sie unterschiedliche Unterlagen. Für eine 
                    Ummeldung beispielsweise Ihren Personalausweis und die Wohnungsgeberbestätigung. 
                    Für eine Melderegisterauskunft über AdressFinder.org benötigen Sie lediglich 
                    die persönlichen Daten der gesuchten Person.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default EinwohnermeldeamtIndex;

